import axios from 'axios';

const ADMIN_URL = process.env.VUE_APP_ADMIN_URL;

function getHeaders() {
  const token = JSON.parse(localStorage.getItem('accessToken'));
  return { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };
}

// The backend answers errors as { code, message } (GenericError) or, for an
// invalid pricing Sheet, { error: { code, details } }. Both are normalized so
// views only read `code` and `details`.
function toError(error) {
  const body = error.response?.data ?? {};
  return {
    status: 'error',
    code: body.code ?? body.error?.code ?? body.message ?? 'UNKNOWN',
    details: body.error?.details ?? [],
    httpCode: error.response?.status
  };
}

async function call(method, path, { data, params } = {}) {
  try {
    const response = await axios({
      method,
      url: `${ADMIN_URL}${path}`,
      data,
      params,
      headers: getHeaders()
    });
    return { status: 'ok', data: response.data.data, meta: response.data.meta };
  } catch (error) {
    return toError(error);
  }
}

/** B2B quote builder (pawer-backend spec 094). The backend prices everything. */
export default class B2bQuotesService {
  static getActiveParameters() {
    return call('get', '/pricing/parameters/active');
  }

  static refreshParameters() {
    return call('post', '/pricing/parameters/refresh');
  }

  static listPartnerVariants(channel, brokerId) {
    const params = { channel };
    if (brokerId) params.brokerId = brokerId;
    return call('get', '/pricing/partner-variants', { params });
  }

  /** Pipedrive organizations (prospects and customers). */
  /** Standard prices of a country, tier by tier (Pawer Pricing panel). */
  static getPriceGrid(countryIso) {
    return call('get', '/b2b-quotes/price-grid', { params: { countryIso } });
  }

  /** What active clients of a country pay for Wellness- and Urgencias-like plans. */
  /** Asistencias compares with Asistencias contracts of the same kind of product (pawer-backend spec 123). */
  static getMarketReferences(countryIso, { line = 'employee_benefit', petProduct = false } = {}) {
    return call('get', '/b2b-quotes/market-references', { params: { countryIso, line, petProduct } });
  }

  static searchClients(search) {
    return call('get', '/b2b-quotes/clients', { params: { search } });
  }

  /** Creates the organization in Pipedrive, with its country. */
  static createClient(name, countryIso) {
    return call('post', '/b2b-quotes/clients', { data: { name, countryIso } });
  }

  /** A Pipedrive organization with its country ("País" field). */
  static getClient(pipedriveOrgId) {
    return call('get', `/b2b-quotes/clients/${pipedriveOrgId}`);
  }

  /** Fills in Pipedrive the country of an organization that has none. */
  static setClientCountry(pipedriveOrgId, countryIso) {
    return call('patch', `/b2b-quotes/clients/${pipedriveOrgId}/country`, { data: { countryIso } });
  }

  /** Open Pipedrive deals of an organization, to choose the one the proposal belongs to. */
  static listDeals(pipedriveOrgId) {
    return call('get', `/b2b-quotes/clients/${pipedriveOrgId}/deals`);
  }

  /** Active Pipedrive pipelines, for the deal a proposal creates. */
  static listPipelines() {
    return call('get', '/b2b-quotes/pipedrive/pipelines');
  }

  static createPartnerVariant(variant) {
    return call('post', '/pricing/partner-variants', { data: variant });
  }

  static preview(input) {
    return call('post', '/b2b-quotes/preview', { data: input });
  }

  static create(body) {
    return call('post', '/b2b-quotes', { data: body });
  }

  static updateDraft(id, body) {
    return call('put', `/b2b-quotes/${id}`, { data: body });
  }

  static finalize(id) {
    return call('post', `/b2b-quotes/${id}/finalize`);
  }

  /**
   * "Generar Propuesta" (spec 102): creates the Google Slides proposal of a
   * saved draft and sends it. `meta.warnings` may carry PROPOSAL_NOT_CONFIGURED, PIPEDRIVE_DEAL_FAILED and PIPEDRIVE_NOTE_FAILED.
   */
  static generateProposal(id) {
    return call('post', `/b2b-quotes/${id}/proposal`);
  }

  /** "Editar" a sent quote: its next version, with what the client asked to change. */
  static createVersion(id, reason, comment) {
    return call('post', `/b2b-quotes/${id}/versions`, { data: { reason, comment } });
  }

  static changeStatus(id, status, reason = null, comment = null) {
    return call('patch', `/b2b-quotes/${id}/status`, { data: { status, reason, comment } });
  }

  static list(filters) {
    return call('get', '/b2b-quotes', { params: filters });
  }

  static get(id) {
    return call('get', `/b2b-quotes/${id}`);
  }
}
